import {
  CloseSmIcon,
  CollapseMdIcon,
  FullScreenMdIcon,
  IconButton,
} from '@crono-one/design-system';
import DragIcon from 'crono-fe-common/icons/DragIcon';
import { useCallback, useRef, useState } from 'react';
import { cn } from 'utils/style-utils';
import { Rnd } from 'react-rnd';

const IFRAME_BASE_WIDTH = 376;
const MIN_WIDTH = 280;
const MIN_HEIGHT = 447;

interface CallFloatingUIBaseProps {
  logo: string;
  logoAlt: string;
  title: string;
  containerId: string;
  isFloatingUIVisible: boolean;
  isCallActive: boolean;
  closeFloatingUI: () => void;
}

export const CallFloatingUIBase = ({
  logo,
  logoAlt,
  title,
  containerId,
  isFloatingUIVisible,
  isCallActive,
  closeFloatingUI,
}: CallFloatingUIBaseProps) => {
  const [iframePointerEvents, setIframePointerEvents] = useState(true);
  const [wrapperSize, setWrapperSize] = useState({ width: 376, height: 560 });
  const observerRef = useRef<ResizeObserver | null>(null);
  const [isExpandedUI, setIsExpandedUI] = useState(true);

  const wrapperRef = useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el) return;
    observerRef.current = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setWrapperSize({ width, height });
    });
    observerRef.current.observe(el);
  }, []);

  const scale = wrapperSize.width / IFRAME_BASE_WIDTH;
  const innerHeight = wrapperSize.height / scale;

  const [size, setSize] = useState<{
    width: number | string;
    height: number | string;
  }>({
    width: 376,
    height: 600,
  });

  const [position, setPosition] = useState({
    x: 14,
    y: 14,
  });

  return (
    <Rnd
      position={position}
      size={
        isExpandedUI
          ? size
          : {
              width: size.width,
              height: 'auto',
            }
      }
      onDragStart={() => setIframePointerEvents(false)}
      onDragStop={(e, data) => {
        setIframePointerEvents(true);
        setPosition({
          x: data.x,
          y: data.y,
        });
      }}
      onResizeStart={() => setIframePointerEvents(false)}
      onResizeStop={(e, direction, ref, delta, newPosition) => {
        setIframePointerEvents(true);
        setSize({
          width: ref.style.width,
          height: ref.style.height,
        });
        setPosition(newPosition);
      }}
      bounds="window"
      className={cn(
        'fixed z-1500 top-0 left-0',
        !isFloatingUIVisible && 'hidden!',
      )}
      lockAspectRatio
      enableResizing={isExpandedUI}
      minWidth={MIN_WIDTH}
      minHeight={MIN_HEIGHT}
    >
      <div className="bg-white border-2 border-neutral-400 rounded-3xl flex flex-col size-full">
        <div
          className={cn(
            'grid grid-cols-[1fr_auto_1fr] gap-2 px-3 items-center h-11',
            isExpandedUI && 'border-b border-neutral-300',
          )}
        >
          <IconButton
            icon={CloseSmIcon}
            label={`Close ${title} UI`}
            size="xs"
            variant="light"
            tone="lightGray"
            shape="rounded"
            disabled={isCallActive}
            onClick={closeFloatingUI}
          />

          <div className="flex items-center gap-2 justify-center min-w-0">
            <img
              src={logo}
              alt={logoAlt}
              className="size-5 rounded-full shrink-0"
            />
            <p className="font-semibold text-gray-900 truncate">{title}</p>
            {isCallActive && (
              <div className="shrink-0 whitespace-nowrap bg-gray-200 font-medium text-blue-600 px-1 py-0.5 text-3xs rounded-lg">
                Calling...
              </div>
            )}
          </div>

          <div className="flex items-center justify-end">
            {isExpandedUI ? (
              <IconButton
                icon={CollapseMdIcon}
                label={`Collapse ${title} UI`}
                size="xs"
                variant="ghost"
                shape="rounded"
                onClick={() => setIsExpandedUI(false)}
              />
            ) : (
              <IconButton
                icon={FullScreenMdIcon}
                label={`Expand ${title} UI`}
                size="xs"
                variant="ghost"
                shape="rounded"
                onClick={() => setIsExpandedUI(true)}
              />
            )}

            <IconButton
              icon={DragIcon}
              label={`Drag to move ${title} UI`}
              size="xs"
              variant="ghost"
              shape="rounded"
              className="handle cursor-grab active:cursor-grabbing"
            />
          </div>
        </div>

        <div
          ref={wrapperRef}
          className={cn(
            'relative flex-1 overflow-hidden bg-neutral-200 rounded-b-3xl',
            !isExpandedUI && 'hidden!',
          )}
        >
          <div
            id={containerId}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: IFRAME_BASE_WIDTH,
              height: innerHeight,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
            }}
            className={cn(
              '[&>iframe]:rounded-b-2xl [&>iframe]:border-none',
              !iframePointerEvents && 'pointer-events-none',
            )}
          />
        </div>
      </div>
    </Rnd>
  );
};
